import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import {
  DataTitle,
  DataValue,
  NoData,
  PageCard,
  PageContainer,
  PageFrameworkLibSkeleton,
  Poppins,
  Spacer,
  TitleWithBack,
} from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import { useParams } from 'react-router';
import GenericTable from 'src/components/GenericTable';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { ComplianceFrameworkQuery } from 'src/graphql/_generated';
import _ from 'lodash';
import { isQueryLoading } from 'src/graphql';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { ComplienceChart } from 'src/components/charts';
import { useColumns } from './useColumns';
import { ExpandContent } from './comps';
import { downloadFrameworkControlsCsv } from './export';
import { COMPLIANCE_FRAMEWORK } from 'src/graphql/schemas/queries/data';
import { FrameworkImportModal } from './FrameworkImportModal';

const Div = styled(PageContainer)`
  .content {
    display: grid;
    grid-gap: 20px;

    &__row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr;
      grid-gap: 10px;
      align-items: center;
    }
  }
  .btn-container {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    .content {
      grid-gap: 10px;
      &__row {
        grid-template-columns: 1fr;
      }
    }
  }
`;

type ComplianceControlItem = ComplianceFrameworkQuery['complianceFrameworks'][number]['complianceControls'][number] & {
  frameworkControl: ComplianceFrameworkQuery['complianceFrameworks'][number]['complianceControls'][number]['frameworkControl'] & {
    shortname?: string | null;
    strategy?: string | null;
    description?: string | null;
  };
};

type ComplianceFrameworkPageQuery = {
  complianceFrameworks: Array<{
    controlsCount: number;
    controlsImplementedCount: number;
    controlsPlannedCount: number;
    controlFramework: ComplianceFrameworkQuery['complianceFrameworks'][number]['controlFramework'] & {
      framework_identifier?: string | null;
      version?: string | null;
      publishDate?: string | null;
    };
    complianceControls: ComplianceControlItem[];
  }>;
};

const FrameworkLibrabryPage9 = () => {
  const { frameworklibraryId } = useParams<{ frameworklibraryId: string }>();
  const [isNewSelection, setIsNewSelection] = useState(true);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { setLocationPaths } = useActionsApp();
  const { workspaceId, isPlatformAdmin } = useAuth();

  useGaEventPageView('Cydea | Framework');

  const { data, loading, networkStatus, error, refetch } = useQuery<ComplianceFrameworkPageQuery>(
    COMPLIANCE_FRAMEWORK,
    {
      variables: {
        workspaceId,
        id: frameworklibraryId!,
      },
    },
  );
  const isLoading = isQueryLoading({ loading, data, networkStatus });
  const complianceFrameworkData = data?.complianceFrameworks[0];
  const frameworkData = complianceFrameworkData?.controlFramework;
  const controlLibs = _.orderBy(complianceFrameworkData?.complianceControls, ['frameworkControl.control_identifier']);
  const canManageContent = isPlatformAdmin;

  useEffect(() => {
    if (frameworkData) {
      setLocationPaths({
        framework: { id: frameworkData.id, name: frameworkData.shortname || frameworkData.name },
      });
    }
    return () => {
      setLocationPaths({ framework: undefined });
    };
  }, [frameworkData]);

  const { columns, GTColumnSelectAnchorExported, checked, setChecked, addToRmp, isAddLoading } = useColumns({
    data: controlLibs,
    isCheckerEnabled: true,
    tdm: {
      onAddToRMPClick: true,
    },
    opensSameTab: true,
    tableId: TableIds.controlLibFramework,
  });

  useEffect(() => {
    if (checked.length) {
      setIsNewSelection(true);
    }
  }, [checked]);

  return (
    <>
      <Meta title={`Cydea | Framework`} feedbackScript />
      {error && (
        <Div>
          <Poppins color="error">{error.message}</Poppins>
        </Div>
      )}
      {!isLoading ? (
        <>
          {frameworkData && (
            <Div data-cy="framework-library-page">
              <PageCard>
                <TitleWithBack title="FRAMEWORK" gradient />
                <Spacer $px={30} />
                {frameworkData.shortname ? (
                  <Poppins px={32} weight={600}>
                    {frameworkData.shortname}
                  </Poppins>
                ) : (
                  <NoData />
                )}
                <Spacer $px={44} />

                <div className="content">
                  <div className="content__row">
                    <div>
                      <DataTitle>FULL NAME</DataTitle>
                      {frameworkData.name ? (
                        <DataValue data-cy="scenario-source">{frameworkData.name}</DataValue>
                      ) : (
                        <NoData />
                      )}
                    </div>
                    <div>
                      <DataTitle>AUTHOR</DataTitle>
                      {frameworkData.author ? (
                        <DataValue data-cy="scenario-event">{frameworkData.author}</DataValue>
                      ) : (
                        <NoData />
                      )}
                    </div>
                    <ComplienceChart
                      controlsCount={complianceFrameworkData?.controlsCount ?? 0}
                      controlsImplementedCount={complianceFrameworkData?.controlsImplementedCount ?? 0}
                      controlsPlannedCount={complianceFrameworkData?.controlsPlannedCount ?? 0}
                      animation
                    />
                  </div>
                  <div>
                    <DataTitle>DESCRIPTION</DataTitle>
                    {frameworkData.description ? <ExpandCollapse text={frameworkData.description} /> : <NoData />}
                  </div>
                </div>
              </PageCard>
              <Spacer $px={30} />
              <div className="btn-container">
                {canManageContent && frameworkData && (
                  <Button
                    secondary
                    onClick={() => setIsImportOpen(true)}
                    $constWidth={190}
                    data-cy="import-framework-csv"
                  >
                    IMPORT CSV
                  </Button>
                )}
                {canManageContent && frameworkData && (
                  <Button
                    secondary
                    onClick={() =>
                      downloadFrameworkControlsCsv({
                        framework: frameworkData,
                        controls: controlLibs || [],
                      })
                    }
                    $constWidth={190}
                    data-cy="export-framework-csv"
                  >
                    EXPORT CSV
                  </Button>
                )}
                <Button
                  primary
                  disabled={!checked.length || isAddLoading}
                  loading={isAddLoading}
                  onClick={() => {
                    const count = checked.length;
                    addToRmp(checked).then(() => {
                      setChecked([]);
                      setIsNewSelection(false);
                      showSuccessToast(count === 1 ? toastMessages.control.addedToRmp : toastMessages.control.addedToRmpPlural);
                    });
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Add to RMP',
                      tags: ['RMP'],
                    });
                  }}
                  $constWidth={200}
                  data-cy="add-to-rmp"
                >
                  {isNewSelection ? '+ ADD TO RMP' : 'ADDED TO RMP'}
                </Button>
              </div>
              {canManageContent && frameworkData && (
                <FrameworkImportModal
                  isOpen={isImportOpen}
                  onClose={() => setIsImportOpen(false)}
                  onImported={async () => {
                    await refetch();
                  }}
                  frameworkId={frameworkData.id}
                  frameworkName={frameworkData.shortname || frameworkData.name}
                  existingControlIds={(controlLibs || []).map((control) => control.frameworkControl.id)}
                />
              )}
              <Spacer $px={30} />
              <GenericTable
                tableId={TableIds.controlLibFramework}
                data={controlLibs}
                columns={columns}
                GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                expandContent={(row) => {
                  if (!row.compliantRmpControls.length) return null;
                  return <ExpandContent controls={row.compliantRmpControls} />;
                }}
                searchable
              />
            </Div>
          )}
        </>
      ) : (
        <Div>
          <PageFrameworkLibSkeleton />
        </Div>
      )}
    </>
  );
};

export default FrameworkLibrabryPage9;
